import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Search, Users, Flame, UserRound, IdCard } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import TablePagination from "../components/TablePagination";

const MONTHS = ["Jan", "Feb", "Mac", "Apr", "Mei", "Jun", "Jul", "Ogo", "Sep", "Okt", "Nov", "Dis"];
const WEEKDAYS = ["Ahd", "Isn", "Sel", "Rab", "Kha", "Jum", "Sab"];
const DAYS = 14;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function DriverAvailability() {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [start, setStart] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });

  useEffect(() => {
    api.get("/users").then((r) => setDrivers(r.data.filter((u) => u.role === "pemandu")));
    api.get("/bookings").then((r) => setBookings(r.data.filter((b) => ["diluluskan", "selesai", "menunggu_kelulusan"].includes(b.status) && b.assigned_pemandu_id)));
  }, []);
  useEffect(() => { setPage(1); }, [search]);

  const days = useMemo(() => Array.from({ length: DAYS }, (_, i) => {
    const d = new Date(start); d.setDate(start.getDate() + i); return d;
  }), [start]);

  const byDriver = useMemo(() => {
    const m = {};
    for (const b of bookings) (m[b.assigned_pemandu_id] = m[b.assigned_pemandu_id] || []).push(b);
    return m;
  }, [bookings]);

  const todayIso = iso(new Date());
  const ym = todayIso.slice(0, 7);
  const yy = todayIso.slice(0, 4);
  const util = useMemo(() => {
    const month = {}, year = {};
    for (const b of bookings) {
      const k = (b.start_datetime || "").slice(0, 7);
      if (k === ym) month[b.assigned_pemandu_id] = (month[b.assigned_pemandu_id] || 0) + 1;
      if (k.slice(0, 4) === yy) year[b.assigned_pemandu_id] = (year[b.assigned_pemandu_id] || 0) + 1;
    }
    return { month, year };
  }, [bookings, ym, yy]);

  const onDutyToday = useMemo(() => new Set(bookings.filter((b) => b.status === "diluluskan" &&
    (b.start_datetime || "").slice(0, 10) <= todayIso && todayIso <= (b.end_datetime || "").slice(0, 10)).map((b) => b.assigned_pemandu_id)), [bookings, todayIso]);
  const activeMonth = drivers.filter((d) => (util.month[d.id] || 0) > 0).length;
  const idleMonth = drivers.length - activeMonth;
  const expired = drivers.filter((d) => d.license_expiry && d.license_expiry < todayIso).length;

  const rank = (counts) => {
    const rows = drivers.map((d) => ({ nama: d.name, n: counts[d.id] || 0 }));
    const s = [...rows].sort((a, b) => b.n - a.n);
    return { top: s.slice(0, 3), least: s.slice(-3).reverse() };
  };
  const mRank = rank(util.month);
  const yRank = rank(util.year);
  const maxMonth = Math.max(1, ...drivers.map((d) => util.month[d.id] || 0));

  const filtered = drivers.filter((d) => {
    const q = search.toLowerCase();
    return !q || `${d.name} ${d.staff_id || ""}`.toLowerCase().includes(q);
  });
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  const cellFor = (d, day) => {
    const key = iso(day);
    const b = (byDriver[d.id] || []).find((x) => (x.start_datetime || "").slice(0, 10) <= key && key <= (x.end_datetime || "").slice(0, 10));
    if (b) {
      return b.status === "selesai"
        ? { cls: "bg-sky-300 hover:bg-sky-400 cursor-pointer", title: `Selesai: ${b.vehicle_reg} → ${b.destination}`, booking: b }
        : { cls: "bg-[#10056E] hover:bg-[#2a17a8] cursor-pointer", title: `Bertugas: ${b.vehicle_reg} → ${b.destination}`, booking: b };
    }
    return { cls: "bg-emerald-300/70 hover:bg-emerald-300", title: "Tersedia" };
  };

  const shift = (n) => setStart((s) => { const d = new Date(s); d.setDate(d.getDate() + n); return d; });

  return (
    <div className="space-y-6 fade-up" data-testid="driver-availability-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Users className="h-6 w-6 text-[#10056E]" /> Ketersediaan Pemandu</h1>
        <p className="text-sm text-muted-foreground mt-1">Pantau tenaga kerja pemandu — {filtered.length} pemandu. Klik sel biru untuk melihat tugasan.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="driver-availability-stats">
        {[
          { label: "Bertugas Hari Ini", value: onDutyToday.size, cls: "border-[#10056E]/30 bg-indigo-50", txt: "text-[#10056E]" },
          { label: "Tersedia Hari Ini", value: drivers.length - onDutyToday.size, cls: "border-emerald-300 bg-emerald-50", txt: "text-emerald-700" },
          { label: "Tiada Tugasan Bulan Ini", value: idleMonth, cls: "border-amber-300 bg-amber-50", txt: "text-amber-700" },
          { label: "Lesen Tamat Tempoh", value: expired, cls: expired > 0 ? "border-red-300 bg-red-50" : "border-slate-200 bg-white", txt: expired > 0 ? "text-[#DC3545]" : "text-slate-800" },
        ].map((s) => (
          <div key={s.label} className={`border rounded-xl px-4 py-3 ${s.cls}`}>
            <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">{s.label}</p>
            <p className={`text-2xl font-bold ${s.txt}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-testid="driver-availability-utilization">
        {[{ t: "Bulan Ini", r: mRank }, { t: "Tahun Ini", r: yRank }].map(({ t, r }) => (
          <div key={t} className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <p className="text-sm font-bold flex items-center gap-2"><Flame className="h-4 w-4 text-[#DC3545]" /> Beban Tugasan {t}</p>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <p className="text-[10px] uppercase tracking-wide font-semibold text-[#DC3545]">Paling Banyak Tugasan</p>
                {r.top.map((x) => (
                  <div key={x.nama} className="flex items-center justify-between text-xs mt-1.5 gap-2">
                    <span className="font-semibold truncate">{x.nama}</span>
                    <span className="font-bold text-[#DC3545] bg-red-50 border border-red-200 rounded-full px-2 py-0.5 shrink-0">{x.n}x</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide font-semibold text-emerald-600">Paling Kurang (keutamaan)</p>
                {r.least.map((x) => (
                  <div key={x.nama} className="flex items-center justify-between text-xs mt-1.5 gap-2">
                    <span className="font-semibold truncate">{x.nama}</span>
                    <span className="font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 shrink-0">{x.n}x</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shift(-7)} data-testid="driver-timeline-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" className="h-9" onClick={() => { const d = new Date(); d.setHours(0, 0, 0, 0); setStart(d); }} data-testid="driver-timeline-today-btn">Hari Ini</Button>
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shift(7)} data-testid="driver-timeline-next-btn"><ChevronRight className="h-4 w-4" /></Button>
            <p className="text-sm font-semibold ml-1">
              {days[0].getDate()} {MONTHS[days[0].getMonth()]} — {days[DAYS - 1].getDate()} {MONTHS[days[DAYS - 1].getMonth()]} {days[DAYS - 1].getFullYear()}
            </p>
          </div>
          <div className="relative w-56">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9 h-9" placeholder="Cari nama / no. staf..." value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="driver-timeline-search-input" />
          </div>
        </div>

        <TablePagination page={page} setPage={setPage} perPage={perPage} setPerPage={setPerPage} total={filtered.length} />

        <div className="px-4 py-2 flex flex-wrap gap-4 text-[11px] text-muted-foreground border-b">
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-emerald-300/70 border border-emerald-400" /> Tersedia</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-[#10056E]" /> Bertugas</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-sky-300" /> Selesai</span>
          <span className="flex items-center gap-1.5">Bar beban: <span className="font-semibold">merah = beban tinggi</span>, biru = sederhana, hijau = ringan/tiada</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[980px]">
            <thead>
              <tr className="bg-slate-50/70">
                <th className="text-left text-xs font-semibold text-muted-foreground px-3 py-1.5 sticky left-0 bg-slate-50 z-10 w-64 border-b">Pemandu</th>
                {days.map((d) => (
                  <th key={iso(d)} className={`text-center text-[10px] font-semibold px-1 py-1.5 border-b border-l ${iso(d) === todayIso ? "bg-indigo-50 text-[#10056E]" : "text-muted-foreground"}`}>
                    {WEEKDAYS[d.getDay()]}<br />{d.getDate()} {MONTHS[d.getMonth()]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 && (
                <tr><td colSpan={DAYS + 1} className="text-center text-sm text-muted-foreground py-10">Tiada pemandu dijumpai.</td></tr>
              )}
              {paged.map((d) => {
                const n = util.month[d.id] || 0;
                const lic = d.license_expiry && d.license_expiry < todayIso;
                const state = lic
                  ? `Lesen Tamat Tempoh (${d.license_expiry})`
                  : onDutyToday.has(d.id) ? "Bertugas hari ini" : "Tersedia hari ini";
                return (
                  <tr key={d.id} className="border-b last:border-b-0 hover:bg-slate-50/50" data-testid={`driver-timeline-row-${d.staff_id || d.id}`}>
                    <td className="px-3 py-1 sticky left-0 bg-white z-10 border-r">
                      <div className="flex items-center gap-2">
                        {d.photo_thumb ? (
                          <img src={d.photo_thumb} alt={d.name} className="h-7 w-7 rounded-full object-cover border shrink-0" />
                        ) : (
                          <span className="h-7 w-7 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center text-[#10056E] shrink-0"><UserRound className="h-3.5 w-3.5" /></span>
                        )}
                        <div className="min-w-0" title={`${state} · ${n} tugasan bulan ini`} data-testid={`driver-state-${d.staff_id || d.id}`}>
                          <p className="text-[13px] font-bold truncate leading-tight">{d.name}
                            {lic && <IdCard className="h-3.5 w-3.5 text-[#DC3545] inline ml-1.5" title="Lesen tamat tempoh" />}
                          </p>
                          <div className="flex items-center gap-1.5 mt-px">
                            <span className="text-[10px] text-muted-foreground">{d.staff_id}</span>
                            <span className="h-1.5 w-16 bg-slate-100 rounded-full overflow-hidden">
                              <span className={`block h-full rounded-full ${n / maxMonth > 0.66 ? "bg-[#DC3545]" : n > 0 ? "bg-[#10056E]" : "bg-emerald-400"}`} style={{ width: `${Math.max(6, (n / maxMonth) * 100)}%` }} />
                            </span>
                            <span className="text-[10px] font-bold text-slate-600">{n}x</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    {days.map((day) => {
                      const c = cellFor(d, day);
                      return (
                        <td key={iso(day)} className="border-l p-px">
                          <div title={c.title} onClick={() => c.booking && navigate(`/tempahan/${c.booking.id}`)}
                            className={`h-6 rounded-sm transition-colors ${c.cls}`} />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
